import React from 'react';
import { Checkbox, Row, Col, Typography } from 'antd';
import styled from 'styled-components';
import { COLORS } from '../../constants/colors';

const { Text } = Typography;

const PermissionContainer = styled.div`
  margin-top: 16px;
  padding: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background-color: #fafafa;
`;

const PermissionTitle = styled(Text)`
  font-weight: 600;
  color: ${COLORS.textDark};
  font-size: 14px;
  margin-bottom: 16px;
  display: block;
`;

const PermissionCheckbox = styled(Checkbox)`
  .ant-checkbox-wrapper {
    margin-bottom: 8px;
  }
  
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${COLORS.$primaryBlue};
    border-color: ${COLORS.$primaryBlue};
  }
  
  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: ${COLORS.$primaryBlue};
  }

  /* Force checkbox to align to the top */
  &.ant-checkbox-wrapper {
    align-items: flex-start !important;
  }

  .ant-checkbox {
    align-self: flex-start;
    margin-top: 0 !important;
    top: 0 !important;
  }

  /* Ensure the label content wraps properly */
  .ant-checkbox + span {
    padding-top: 0;
    padding-left: 8px;
  }
`;

const DisabledText = styled(Text)`
  color: ${COLORS.textMedium};
  font-size: 12px;
  font-style: italic;
  margin-top: 8px;
  display: block;
`;

const AVAILABLE_PERMISSIONS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    description: 'Access to admin dashboard and analytics'
  },
  {
    key: 'event_management',
    label: 'Event Management',
    description: 'Create, edit, and manage events and bookings'
  },
  {
    key: 'patient_queue',
    label: 'Intake / Patient Queue',
    description: 'View and manage the patient intake queue'
  },
  {
    key: 'screening_data',
    label: 'Members / Screening Data',
    description: 'View and manage member records and screening data'
  },
  {
    key: 'case_management',
    label: 'Case Management',
    description: 'Manage high-risk patient cases and outreach'
  },
  {
    key: 'messaging',
    label: 'Messaging / Inbox',
    description: 'View and send SMS conversations (Inbox and member Messaging tab)'
  },
  {
    key: 'user_management',
    label: 'User Management',
    description: 'Create, edit, and manage users (also unlocks Form Config & Data Import)'
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Access analytics dashboards (Reports, Health Outcomes, Operations, etc.)'
  },
  {
    key: 'audit_logs',
    label: 'Audit Logs',
    description: 'View system audit logs'
  },
  {
    key: 'voice_calls',
    label: 'Voice Calls / Call Logs',
    description: 'Initiate AI voice agent calls and view call logs'
  },
  {
    key: 'ondemand_management',
    label: 'Deployed Hearts (On-Demand)',
    description: 'Manage on-demand healthcare regions, storage, HCWs, and events'
  },
  {
    key: 'insurance_enrollment',
    label: 'Insurance Enrollments',
    description: 'View health insurance enrollment forms, including SSNs and photo IDs (every view is audit logged)'
  }
];

// grantable: permission keys the signed-in user may hand out (null = all).
// The API rejects granting anything outside it, so the rest are shown locked.
const PermissionSelector = ({ 
  value = [], 
  onChange, 
  role, 
  disabled = false,
  grantable = null
}) => {
  const isAdmin = role === 'admin';
  const isPermissionDisabled = disabled || isAdmin;
  const canGrant = (key) => !grantable || grantable.includes(key);

  const handlePermissionChange = (permissionKey, checked) => {
    if (isPermissionDisabled || !canGrant(permissionKey)) return;

    let newPermissions;
    if (checked) {
      newPermissions = [...value, permissionKey];
    } else {
      newPermissions = value.filter(p => p !== permissionKey);
    }
    onChange(newPermissions);
  };

  const handleSelectAll = (checked) => {
    if (isPermissionDisabled) return;

    const locked = value.filter(p => !canGrant(p));
    if (checked) {
      onChange([...locked, ...AVAILABLE_PERMISSIONS.map(p => p.key).filter(canGrant)]);
    } else {
      onChange(locked);
    }
  };

  // For admins, show all permissions as selected and disabled
  const effectivePermissions = isAdmin 
    ? AVAILABLE_PERMISSIONS.map(p => p.key)
    : value;

  const allSelected = effectivePermissions.length === AVAILABLE_PERMISSIONS.length;

  return (
    <PermissionContainer>
      <PermissionTitle>Access Permissions</PermissionTitle>
      
      {isAdmin && (
        <DisabledText>
          Admin users have access to all features automatically.
        </DisabledText>
      )}

      <Row style={{ marginTop: '12px' }}>
        <Col span={24}>
          <PermissionCheckbox
            checked={allSelected}
            indeterminate={!allSelected && effectivePermissions.length > 0}
            onChange={(e) => handleSelectAll(e.target.checked)}
            disabled={isPermissionDisabled}
            style={{ fontWeight: 'bold', marginBottom: '12px' }}
          >
            Select All Permissions
          </PermissionCheckbox>
        </Col>
      </Row>

      <Row gutter={[0, 8]}>
        {AVAILABLE_PERMISSIONS.map((permission) => (
          <Col span={24} key={permission.key}>
            <PermissionCheckbox
              checked={effectivePermissions.includes(permission.key)}
              onChange={(e) => handlePermissionChange(permission.key, e.target.checked)}
              disabled={isPermissionDisabled || !canGrant(permission.key)}
              style={{ alignItems: 'flex-start' }}
            >
              <div style={{ paddingTop: '0px' }}>
                <Text strong style={{ display: 'block', lineHeight: '22px' }}>{permission.label}</Text>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                  {permission.description}
                </Text>
              </div>
            </PermissionCheckbox>
          </Col>
        ))}
      </Row>

      {!isAdmin && (
        <DisabledText style={{ marginTop: '12px' }}>
          Note: Case Management and Voice Calls permissions work together for AI outreach features.
        </DisabledText>
      )}
    </PermissionContainer>
  );
};

export default PermissionSelector;
